import {
  ApplySchemaAttributes,
  command,
  CommandFunction,
  extension,
  ExtensionTag,
  GetMarkRange,
  Handler,
  MarkExtension,
  MarkExtensionSpec,
  MarkSpecOverride,
  StateUpdateLifecycleProps,
} from 'remirror';
import { Node } from '@remirror/pm/dist-types/model';
import { EditorState, Transaction } from '@remirror/pm/state';
import { ReplacementSourceEnum } from '@replacements/utils/floatingReplacementsToolbarUtils';
import { replacementRegex } from '@replacements/context/phoneticReplacement';
import XRegExp from '@replacements/utils/XRegExp';
import { EnhancedPhoneticReplacement } from 'store/ReplacementsSlice';
import { captureExceptionWithState } from 'util/sentry';
import {
  handleReplaceSelectedText,
  handleWrapSelectedTextInQuotes,
} from 'components/EditorV2/extensions/ReplacementUtils';
import { PhoneticReplacement } from '../../../../../server/graphql/__generated.types';
import {
  isRespellingFromType,
  ReplacementType,
  STABLE_REPLACEMENT_TAG_NAME,
} from '../utils/transforms';

export const REPLACEMENT_TAG_NAME = 'phonetic';

interface ReplacementPosition {
  text: string;
  replacement: string;
  from: number;
  to: number;
  isRespelling: boolean;
  source: ReplacementSourceEnum;
  entryId: string;
}

export interface ReplacementOptions {
  className: string;
  isEnabled: boolean;
  onChange?: Handler<() => void>;
  onDocChange?: Handler<(prevDoc: Node, nextDoc: Node) => void>;
}

export const defaultOptions = {
  className: '',
  isEnabled: true,
};

@extension<ReplacementOptions>({
  defaultOptions,
  handlerKeys: ['onChange', 'onDocChange'],
  staticKeys: [],
  customHandlerKeys: [],
})

/*
 * Maintains replacement marks, bolded words containing replacement attributes, in editor
 * Built to use for a list of controlled remirror editors, namely script first studio
 */
export class ReplacementExtension extends MarkExtension<ReplacementOptions> {
  tagName = REPLACEMENT_TAG_NAME;

  className = this.options.className ?? '';

  get name() {
    return this.tagName;
  }

  createTags() {
    return [ExtensionTag.FormattingMark];
  }

  createMarkSpec(
    extra: ApplySchemaAttributes,
    override: MarkSpecOverride
  ): MarkExtensionSpec {
    return {
      ...override,
      // phonetic mark will not be applied to content inserted at the end of the mark
      inclusive: false,
      attrs: {
        ...extra.defaults(),
        replacement: { default: '' },
        // for use in removing marks when editor text deviates for original text
        originalText: { default: '' },
        // Flag whether this replacement is a respelling, necessary for
        // serializing to XML
        isRespelling: { default: false },
        // controls bold styling and necessary for serializing to xml
        enabled: { default: true },
        // represents a replacement that has been selected or disabled
        replacementChanged: { default: false },
        // toggled off or deleted replacement, necessary to prevent autoadding
        softDelete: { default: false },
        source: { default: '' },
        entryId: { default: '' },
      },
      parseDOM: [
        {
          tag: this.tagName,
          getAttrs: (dom: HTMLElement) => {
            return {
              ...extra.parse(dom),
              replacement: dom.getAttribute('replacement') ?? '',
              originalText: dom.getAttribute('originalText') ?? '',
              enabled: dom.getAttribute('enabled') !== 'false',
              replacementChanged:
                dom.getAttribute('replacementChanged') === 'true',
              softDelete: dom.getAttribute('softDelete') === 'true',
              isRespelling: dom.getAttribute('isRespelling') === 'true',
              source: dom.getAttribute('source') ?? '',
              entryId: dom.getAttribute('entryId') ?? '',
            };
          },
        },
        {
          tag: 'respell',
          getAttrs: (dom: HTMLElement) => {
            return {
              ...extra.parse(dom),
              replacement: dom.getAttribute('value') ?? '',
              originalText: dom.textContent ?? '',
              enabled: true,
              replacementChanged: false,
              softDelete: false,
              isRespelling: true,
              // default source to us-suggestion
              // note: may not always align with the toolbar
              source: 'us-suggestion',
            };
          },
        },
        {
          tag: STABLE_REPLACEMENT_TAG_NAME,
          getAttrs: (dom: HTMLElement) => {
            const type = (dom.getAttribute('type') ??
              'respell') as ReplacementType;
            const enabled = dom.getAttribute('enabled') !== 'false';
            return {
              ...extra.parse(dom),
              replacement: dom.getAttribute('value') ?? '',
              originalText: dom.textContent ?? '',
              enabled,
              isRespelling: isRespellingFromType(type),
              source: dom.getAttribute('source') ?? '',
              entryId: dom.getAttribute('entryId') ?? '',
            };
          },
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: node => {
        const attrs = {
          ...extra.dom(node),
          class: this.className,
          replacement: node.attrs.replacement,
          originalText: node.attrs.originalText,
          replacementChanged: node.attrs.replacementChanged,
          softDelete: node.attrs.softDelete,
          enabled: node.attrs.enabled,
          isRespelling: node.attrs.isRespelling,
          source: node.attrs.source,
          entryId: node.attrs.entryId,
        };

        return [this.tagName, attrs, 0];
      },
    };
  }

  onStateUpdate({ state, previousState }: StateUpdateLifecycleProps) {
    if (previousState.doc.eq(state.doc) || !this.options.isEnabled) return;

    // surface prev/next docs to hooks that need to inspect what changed
    this.options.onDocChange(previousState.doc, state.doc);
  }

  /*
   * Removes replacement marks whose marked text no longer matches originalText.
   */
  @command()
  removeUnmatchingReplacementMarks(): CommandFunction {
    return ({ view }) => {
      if (!view) return false;

      const { tr, doc } = view.state;
      let removedAny = false;

      doc.descendants((node, pos) => {
        if (!node.isText) return;

        const mark = node.marks.find(m => m.type === this.type);
        if (!mark) return;

        const originalText: string =
          mark.attrs.originalText?.toLowerCase() ?? '';
        if (!originalText) return;

        const end = pos + node.textContent.length;
        const textWithBoundaries = doc
          .textBetween(pos - 1, end + 1)
          .toLowerCase();

        try {
          const match = textWithBoundaries.match(
            replacementRegex(originalText)
          );

          if (!match) {
            tr.removeMark(pos, end, mark);
            removedAny = true;
          }
        } catch {
          captureExceptionWithState(
            new Error('ReplacementExtension: Invalid originalText'),
            {
              type: 'ReplacementExtension',
              value: {
                originalText,
                sectionId: view.dom?.id?.replace(/^section-editor-/, 'unknown'),
              },
            }
          );
        }
      });

      if (!removedAny) return false;

      view.dispatch(tr);
      return true;
    };
  }

  /*
   * Diffs prevDoc vs nextDoc and returns the inserted range in nextDoc.
   * Returns null if there is no diff, or if nothing was inserted (pure deletion).
   */
  private diffRange(
    prevDoc: Node,
    nextDoc: Node
  ): { insertedFrom: number; insertedTo: number; insertedSize: number } | null {
    const diffStart = prevDoc.content.findDiffStart(nextDoc.content);
    if (diffStart === null) return null;

    const diffEnd = prevDoc.content.findDiffEnd(nextDoc.content);
    if (!diffEnd) return null;

    const insertedSize = diffEnd.b - diffEnd.a;
    if (insertedSize <= 0) return null;

    return { insertedFrom: diffStart, insertedTo: diffEnd.b, insertedSize };
  }

  /*
   * Given an insertion point in the doc, walks backwards to find the full word
   * that was just completed. Returns null if there's no word to scan.
   *
   * Words are delimited by whitespace rather than by letters so that a
   * punctuation replacement stays in scope — scanning only the letters of
   * 'and/or' would never surface the slash the user just typed.
   */
  private findWordRange(
    doc: Node,
    insertedTo: number
  ): { wordFrom: number; wordTo: number } | null {
    if (insertedTo <= 1) return null;

    const $pos = doc.resolve(insertedTo - 1);
    const parentStart = $pos.start();
    const parentText = $pos.parent.textContent;
    const isWhitespace = /\s/;

    // Find the word end: walk back past any whitespace (e.g. space just typed)
    let wordEnd = $pos.parentOffset + 1;
    while (wordEnd > 0 && isWhitespace.test(parentText[wordEnd - 1]!)) {
      wordEnd -= 1;
    }

    if (wordEnd === 0) return null;

    // Walk back to find the word start
    let wordStart = wordEnd;
    while (wordStart > 0 && !isWhitespace.test(parentText[wordStart - 1]!)) {
      wordStart -= 1;
    }

    if (wordStart === wordEnd) return null;

    return {
      wordFrom: parentStart + wordStart,
      wordTo: parentStart + wordEnd,
    };
  }

  /*
   * Deduplicates replacements and builds a regex map for use in applyReplacementMarks.
   */
  private buildRegexMap(
    replacements: EnhancedPhoneticReplacement[]
  ): Array<{ replacement: EnhancedPhoneticReplacement; regex: RegExp }> {
    const byText = deduplicateByText(replacements);
    return Array.from(byText.values()).map(replacement => ({
      replacement,
      regex: replacementRegex(replacement.text),
    }));
  }

  private createReplacementMark(replacement: EnhancedPhoneticReplacement) {
    return this.type.create({
      replacement: replacement.replacement,
      originalText: replacement.text,
      isRespelling: replacement.isRespelling,
      source: replacement.libraryId
        ? ReplacementSourceEnum.Team
        : ReplacementSourceEnum.Personal,
      entryId: replacement.id,
    });
  }

  /*
   * Applies replacement marks to text nodes visited by doc.descendants.
   * Optionally clamps to a (from, to) range — pass null to scan the whole doc.
   */
  private applyReplacementMarks(
    updated: Transaction,
    doc: ReturnType<typeof updated.doc.resolve>['doc'],
    regexMap: Array<{
      replacement: EnhancedPhoneticReplacement;
      regex: RegExp;
    }>,
    range: { from: number; to: number } | null = null
  ): void {
    doc.descendants((node, pos) => {
      if (!node.isText) return;
      if (range && (pos + node.nodeSize <= range.from || pos >= range.to))
        return;

      const text = node.text!;

      regexMap.forEach(({ replacement, regex }) => {
        const positions = XRegExp.getLbIgnoreRespellingsPositions(text, regex);

        positions.forEach(({ from: relFrom, to: relTo }) => {
          const absFrom = pos + relFrom;
          const absTo = pos + relTo;

          if (range && (absFrom < range.from || absTo > range.to)) return;
          if (updated.doc.rangeHasMark(absFrom, absTo, this.type)) return;

          updated.addMark(
            absFrom,
            absTo,
            this.createReplacementMark(replacement)
          );
        });
      });
    });
  }

  /*
   * Diffs prevDoc vs nextDoc to find the inserted range, then applies
   * replacement marks to the affected range. Single-char insertions are
   * treated as typing (only fires at word boundaries); larger insertions
   * are treated as paste (scan the full inserted range).
   */
  @command()
  applyReplacementMarksToUserInput(
    prevDoc: Node,
    nextDoc: Node,
    replacements: EnhancedPhoneticReplacement[]
  ): CommandFunction {
    return ({ view }) => {
      if (!view) return false;

      // Adding marks doesn't change textContent, so this prevents re-triggers
      if (prevDoc.textContent === nextDoc.textContent) return false;

      const diff = this.diffRange(prevDoc, nextDoc);
      if (!diff) return false;

      const { insertedFrom, insertedTo, insertedSize } = diff;
      const regexMap = this.buildRegexMap(replacements);
      const updated = view.state.tr;
      const { doc } = view.state;

      if (insertedSize === 1) {
        const word = this.findWordRange(nextDoc, insertedTo);
        if (!word) return false;

        this.applyReplacementMarks(updated, doc, regexMap, {
          from: word.wordFrom,
          to: word.wordTo,
        });
      } else {
        // Paste path: scan the full inserted range
        this.applyReplacementMarks(updated, doc, regexMap, {
          from: insertedFrom,
          to: insertedTo,
        });
      }

      if (updated.steps.length > 0) {
        view.dispatch(updated);
      }
      return true;
    };
  }

  @command()
  updateReplacementForMark(
    markWithRange: GetMarkRange,
    options: Partial<{
      enabled: boolean;
      newReplacement: string;
      originalText: string;
      replacementChanged: boolean;
      isRespelling: boolean;
      source: ReplacementSourceEnum;
      entryId: string;
    }>
  ): CommandFunction {
    return ({ view }) => {
      if (!view) return false;

      const update = view.state.tr;

      const {
        from,
        to,
        mark: { attrs, type },
      } = markWithRange;
      update.removeMark(from, to, type);

      const {
        replacementChanged = attrs.replacementChanged,
        newReplacement = attrs.replacement,
        enabled = attrs.enabled,
        isRespelling = attrs.isRespelling,
        source = attrs.source,
        originalText = attrs.originalText,
        entryId = attrs.entryId,
      } = options;

      update.addMark(
        from,
        to,
        this.type.create({
          replacement: newReplacement,
          originalText,
          enabled,
          replacementChanged,
          isRespelling,
          source,
          entryId,
        })
      );

      view.dispatch(update);

      return true;
    };
  }

  @command()
  replaceSelectedText(replacement: string): CommandFunction {
    return ({ tr, state, dispatch }) => {
      return handleReplaceSelectedText(tr, state, dispatch, replacement);
    };
  }

  @command()
  wrapSelectedTextInQuotes(): CommandFunction {
    return ({ tr, state, dispatch }) => {
      return handleWrapSelectedTextInQuotes(tr, state, dispatch);
    };
  }

  /*
   * Add phonetic marks for all replacement matches in the given state.
   * Accepts a pre-deduped map (keyed by lowercased source word) so callers
   * processing many sections can hoist the dedup cost outside their loop.
   * Skips ranges already marked. Returns the transaction — does not dispatch.
   */
  addReplacementMarksByText(
    state: EditorState,
    byText: Map<string, EnhancedPhoneticReplacement>,
    withRegex?: ReplacementWithRegex[]
  ): Transaction {
    const updated = state.tr;

    // Use pre-built regexes when provided (e.g. bulk ops over many sections),
    // otherwise build now. Either way each regex is built at most once per call.
    const replacementsWithRegex =
      withRegex ?? buildReplacementsWithRegex(byText);

    this.applyReplacementMarks(updated, state.doc, replacementsWithRegex);

    return updated;
  }

  /*
   * Convenience wrapper — deduplicates replacements then calls addReplacementMarksByText.
   */
  addReplacementMarks(
    state: EditorState,
    replacements: EnhancedPhoneticReplacement[]
  ): Transaction {
    return this.addReplacementMarksByText(
      state,
      deduplicateByText(replacements)
    );
  }

  /*
   * Remove phonetic marks whose entryId is in the given set.
   * Only removes enabled marks; leaves disabled marks in place.
   * Returns the transaction — does not dispatch.
   */
  removeReplacementMarks(
    state: EditorState,
    entryIds: Set<string>
  ): Transaction {
    const updated = state.tr;

    state.doc.descendants((node, pos) => {
      if (!node.isText) return;

      const mark = node.marks.find(m => m.type === this.type);
      if (!mark) return;

      const { entryId } = mark.attrs;
      if (!entryId || !entryIds.has(entryId)) return;
      if (mark.attrs.enabled === false) return;

      const from = pos;
      const to = pos + node.textContent.length;
      updated.removeMark(from, to, mark);
    });

    return updated;
  }

  /*
   * Remove every replacement mark in the document, leaving the text it was applied
   * to. Used when swapping to an avatar that cannot render replacements.
   *
   * Unlike clearReplacements, library replacements are not soft deleted - the
   * section no longer supports replacements, so nothing re-applies them.
   */
  @command()
  removeReplacements(): CommandFunction {
    return props => {
      const { state, view, dispatch, tr: providedTr } = props;

      const tr = providedTr || (view ? view.state.tr : state.tr);
      let removedAny = false;

      state.doc.descendants((node, pos) => {
        if (!node.isText) return;

        const replacementMark = node.marks.find(
          mark => mark.type === this.type
        );

        if (!replacementMark) return;

        tr.removeMark(pos, pos + node.textContent.length, replacementMark);

        removedAny = true;
      });

      if (!removedAny) {
        return false;
      }

      if (view && !providedTr) {
        view.dispatch(tr.scrollIntoView());
      } else if (dispatch) {
        dispatch(tr.scrollIntoView());
      }

      return true;
    };
  }
}

export interface ReplacementWithRegex {
  replacement: EnhancedPhoneticReplacement;
  regex: RegExp;
}

/*
 * Build regexes for a pre-deduped byText map. Hoist this outside per-section
 * loops so regexes are built once for the whole bulk operation.
 */
export const buildReplacementsWithRegex = (
  byText: Map<string, EnhancedPhoneticReplacement>
): ReplacementWithRegex[] =>
  [...byText.values()].map(replacement => ({
    replacement,
    regex: replacementRegex(replacement.text),
  }));

/*
 * Deduplicate replacements by source word, keeping the most recently updated per word.
 */
export const deduplicateByText = (
  replacements: EnhancedPhoneticReplacement[]
): Map<string, EnhancedPhoneticReplacement> => {
  const byText = new Map<string, EnhancedPhoneticReplacement>();
  for (const r of replacements) {
    const key = r.text.toLowerCase();
    const existing = byText.get(key);
    if (!existing || r.updatedAt > existing.updatedAt) {
      byText.set(key, r);
    }
  }
  return byText;
};

const getReplacementPositions = (
  replacementsLibrary: EnhancedPhoneticReplacement[],
  text: string
) => {
  let replacementPositions: ReplacementPosition[] = [];

  // remove duplicates
  const filtered: Record<string, PhoneticReplacement> = {};
  replacementsLibrary.forEach(item => {
    if (!filtered[item.text.toLowerCase()]) {
      filtered[item.text.toLowerCase()] = item;
    }
  });

  const filteredReplacements = Object.entries(filtered);

  filteredReplacements.forEach(([, replacement]) => {
    const regex = replacementRegex(replacement.text);

    const positions = XRegExp.getLbIgnoreRespellingsPositions(text, regex);
    replacementPositions = replacementPositions.concat(
      positions.map(index => ({
        text: replacement.text,
        replacement: replacement.replacement,
        from: index.from,
        to: index.to,
        isRespelling: replacement.isRespelling,
        source: replacement.libraryId
          ? ReplacementSourceEnum.Team
          : ReplacementSourceEnum.Personal,
        entryId: replacement.id,
      }))
    );
  });

  return replacementPositions;
};

/*
 * From the given remirror state return a new doc with all marks removed
 */
const getDocWithoutMarks = (state: EditorState): Node => {
  let { tr } = state;
  tr = tr.removeMark(0, state.doc.nodeSize - 2);
  return tr.doc;
};
